import React from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../shared/ui';
import { RECHERCHE_MIN, versClientChoisi, type ClientChoisi, type EtatRechercheClients, type ResultatClient } from './rechercheClients';

/** Repères d'une ligne : numéro du compte klygroupe.com et code client SAGE. */
function reperes(r: ResultatClient): string {
  return [
    r.prestashopId != null ? `Compte boutique n° ${r.prestashopId}` : null,
    r.customerCode ? `Code SAGE ${r.customerCode}` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * Liste fusionnée des clients trouvés (comptes SAV et boutique klygroupe.com).
 * Choisir une ligne ne fait que la SÉLECTIONNER (`onChoisi`) : le compte SAV
 * d'un client de la boutique n'est créé, ou vérifié, qu'à la validation de la
 * fenêtre (assurerCompteSav). Annuler la fenêtre ne laisse donc rien derrière.
 * États : recherche en cours, boutique injoignable (message discret, les
 * comptes SAV restent proposés), aucun résultat (`messageVide`).
 */
export const ListeResultatsClients: React.FC<{
  recherche: string;
  etat: EtatRechercheClients;
  onChoisi: (client: ClientChoisi) => void;
  messageVide: string;
  /** Libellé accessible de la liste. */
  libelle?: string;
}> = ({ recherche, etat, onChoisi, messageVide, libelle = 'Clients trouvés' }) => {
  const { resultats, enCours, enCoursBoutique, boutiqueIndisponible } = etat;
  const assez = recherche.trim().length >= RECHERCHE_MIN;

  return (
    <>
      {assez && (resultats.length > 0 || enCours) && (
        <ul aria-label={libelle} aria-busy={enCours} className="mt-1 max-h-64 overflow-y-auto rounded-lg border border-trait bg-white divide-y divide-trait">
          {enCours && resultats.length === 0 && <li className="px-3 py-2 text-xs text-graphite">Recherche…</li>}
          {resultats.map(r => (
            <li key={r.cle}>
              <button
                type="button"
                onClick={() => onChoisi(versClientChoisi(r))}
                className="w-full text-left px-3 py-2 min-h-[40px] lg:min-h-0 hover:bg-brume focus:bg-brume focus:outline-none"
              >
                <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                  <p className="text-sm font-semibold text-encre break-words min-w-0">{r.titre}</p>
                  {r.compteSavId ? (
                    <Badge className="bg-sapin-100 text-sapin-700 shrink-0">Compte SAV</Badge>
                  ) : (
                    <Badge className="bg-brume text-graphite border border-trait shrink-0">Boutique (compte SAV créé à la validation)</Badge>
                  )}
                </div>
                {r.personne && <p className="text-xs text-encre break-words">{r.personne}</p>}
                {(r.email || r.phone) && <p className="text-xs text-graphite break-all">{r.email}{r.phone ? ` · ${r.phone}` : ''}</p>}
                {reperes(r) && <p className="text-xs text-graphite tnum">{reperes(r)}</p>}
              </button>
            </li>
          ))}
          {enCoursBoutique && resultats.length > 0 && (
            <li className="px-3 py-2 text-xs text-graphite inline-flex items-center gap-1 w-full"><Loader2 className="w-3 h-3 animate-spin" />Recherche dans klygroupe.com…</li>
          )}
        </ul>
      )}
      {assez && boutiqueIndisponible && <p className="mt-1 text-xs text-graphite" role="status">{boutiqueIndisponible}</p>}
      {assez && !enCours && resultats.length === 0 && <p className="mt-1 text-xs text-graphite">{messageVide}</p>}
    </>
  );
};

export default ListeResultatsClients;
